import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';

import { useOffset } from 'Context/LayoutContext/OffsetProvider';
import { get } from 'Components/Base/constants';
import { Box, ButtonFilled, Flex, Text } from 'Components/Base';
import { Modal } from 'Components/Modal';
import useCompatibleDisplay from 'Util/Hooks/useCompatibleDisplay';
import { CustomRadio } from 'Routes/GuidedPractice/Components/ExamRoom/ExamChoice';
import HTMLView from 'Components/HTMLView';

const ContentWrapper = styled(Box)`
  padding: ${get('space.7')}px;
  overflow-y: scroll;

  /* Webkit browsers (Chrome, Safari) */
  &::-webkit-scrollbar {
    display: none;
  }

  /* Firefox */
  scrollbar-width: none;
`;

const StyledButtonFilled = styled((props) => (
  <ButtonFilled
    color="backgroundNeutral"
    variant={props.isMobile ? 'small' : 'medium'}
    {...props}
  />
))`
  ${({ theme, isMobile }) => css`
    border-radius: 300px;
    height: ${isMobile ? '40px' : '48px'};
    &:not(:disabled):hover {
      background-color: ${theme.colors.greyscale.grey1};
    }
  `}
`;

const HeaderWrapper = styled(Flex)`
  ${({ theme }) => css`
    position: sticky;
    top: 0;
    &.solution.correct {
      background-color: ${theme.colors.backgroundGreenLight};
    }

    &.solution.incorrect {
      background-color: ${theme.colors.backgroundYellowLight};
    }
  `}
`;

const SolutionDetailsModal = ({ muiProps, isCorrect, solution, disabledNextButton }) => {
  const { t } = useTranslation('guidedPractice');
  const { isMobile } = useCompatibleDisplay();
  const { isActive, onClose, ...restMuiProps } = muiProps;
  const { topOffset } = useOffset();

  return (
    <Modal
      muiProps={{
        isActive,
        modalPaperStyle: { maxWidth: 864, margin: isMobile ? 16 : 32, height: '100%' },
        innerProps: { height: '100%' },
        styleModalContentProps: { overflow: 'hidden' },
        modalContainerStyle: {
          zIndex: 1000,
          paddingTop: isMobile ? topOffset : 24,
          paddingBottom: isMobile ? topOffset : 24,
        },
      }}
      contentWrapperProps={{
        padding: '0px !important',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
      contentProps={{ style: { flex: 1, overflow: 'auto' } }}
      disableTopOffset
      header={
        <HeaderWrapper
          className={'solution' + (isCorrect ? ' correct' : ' incorrect')}
          alignItems="center"
          gridColumnGap={6}
          flexGrow={1}
          padding={isMobile ? 6 : 7}
          style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}
        >
          <Flex alignItems="center" justifyContent="space-between" flexGrow={1}>
            <Flex gridColumnGap={isMobile ? 4 : 6} alignItems="center">
              <CustomRadio
                className={isCorrect ? 'correct' : 'incorrect'}
                style={{ minWidth: isMobile ? 32 : 40, minHeight: isMobile ? 32 : 40 }}
              />
              <Text textStyle={'heading4'} fontWeight={700}>
                {isCorrect
                  ? t('EXAM_ROOM.RESULT.TEXT.CORRECT')
                  : t('EXAM_ROOM.RESULT.TEXT.INCORRECT')}
              </Text>
            </Flex>
            <StyledButtonFilled isMobile={isMobile} disabled={disabledNextButton} onClick={onClose}>
              {t('BUTTON.GO')}
            </StyledButtonFilled>
          </Flex>
        </HeaderWrapper>
      }
      contents={
        <ContentWrapper>
          <HTMLView rawContent={solution} />
        </ContentWrapper>
      }
      {...restMuiProps}
    />
  );
};

SolutionDetailsModal.propTypes = {
  muiProps: PropTypes.shape({
    toggleComponent: PropTypes.element,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    isActive: PropTypes.bool,
  }),
  isCorrect: PropTypes.bool,
  solution: PropTypes.string,
  disabledNextButton: PropTypes.bool,
};

export default SolutionDetailsModal;
